import { useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { useAiChat } from '@factorialco/f0-react'
import { Plus, Delete } from '@factorialco/f0-react/icons/app'
import {
  IncentivesCompensationPlanDetailDocument,
  IncentivesCompensationPlanPayoutsDocument,
  IncentivesCompensationPlanPayoutFragment,
} from 'generated/resources/graphql'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useOneMutationSubscription } from 'modules/ai/lib/useOneMutationSubscription'
import i18n from 'modules/core/lib/i18n'
import { isRecurrence, recurrenceLabel } from 'modules/incentives/lib/recurrence'
import { compact } from 'lodash'
import { RemoveAssignmentDialog } from 'modules/incentives/components/RemoveAssignmentDialog'

interface CompensationPlanAssignmentsProps {
  periodId: string
  planName: string
  canCreateAssignment: boolean
}

function formatCurrency(cents: number | null | undefined, currency: string): string {
  if (cents == null) return '-'
  return i18n.formatNumber(cents / 100, 'currency', currency)
}

function getUpperLimit(
  payout: IncentivesCompensationPlanPayoutFragment
): number | null | undefined {
  const strategy = payout.contractsAdditionalCompensation?.amountStrategy
  if (strategy?.__typename === 'ContractsVariableAmountStrategy') {
    return strategy.upperLimit
  }
  return null
}

function getCurrency(payout: IncentivesCompensationPlanPayoutFragment): string {
  return payout.employee?.legalEntity?.currency ?? 'EUR'
}

function formatAmount(payout: IncentivesCompensationPlanPayoutFragment): string {
  const upperLimit = getUpperLimit(payout)
  const currency = getCurrency(payout)
  const recurrence = payout.contractsAdditionalCompensation?.recurrence

  const amountStr = formatCurrency(upperLimit, currency)
  if (amountStr === '-') return '-'

  const recurrenceStr = recurrence && isRecurrence(recurrence) ? recurrenceLabel(recurrence) : ''
  return recurrenceStr
    ? i18n.t('incentives.detail.assignments.list.amount_with_recurrence', {
        amount: amountStr,
        recurrence: recurrenceStr,
      })
    : i18n.t('incentives.detail.assignments.list.amount_up_to', { amount: amountStr })
}

export function CompensationPlanAssignments({
  periodId,
  planName,
  canCreateAssignment,
}: CompensationPlanAssignmentsProps) {
  const apolloClient = useApolloClient()

  const [assignmentToRemove, setAssignmentToRemove] = useState<string | null>(null)

  const refetchAssignments = async () => {
    await apolloClient.refetchQueries({
      include: [
        IncentivesCompensationPlanPayoutsDocument,
        IncentivesCompensationPlanDetailDocument,
      ],
    })
  }

  useOneMutationSubscription('AddIncentivePlanAssignments', refetchAssignments)
  useOneMutationSubscription('RemoveIncentivePlanAssignments', refetchAssignments)

  const aiChat = useAiChat()

  const openOne = (message: string) => {
    if (aiChat.enabled) {
      aiChat.setOpen(true)
      aiChat.sendMessage(message)
    }
  }

  const source = useGraphqlDataCollectionSource(
    {
      totalItemSummary: true,
      document: IncentivesCompensationPlanPayoutsDocument,
      perPage: 25,
      mapData: (data) => data.incentives.payoutsConnection.nodes || [],
      mapPagination: (data) => ({
        total: data.incentives.payoutsConnection.totalCount ?? 0,
      }),
      mapVariables: () => ({
        compensationPlanPeriodId: periodId,
      }),
      secondaryActions: {
        expanded: 1,
        actions: () =>
          compact([
            canCreateAssignment && {
              label: i18n.t('incentives.detail.assignments.add_additional_compensation.label'),
              icon: Plus,
              onClick: () =>
                openOne(
                  i18n.t('incentives.detail.assignments.add_additional_compensation.one_prompt', {
                    planName,
                  })
                ),
            },
          ]),
      },
      itemActions: (payout: IncentivesCompensationPlanPayoutFragment) =>
        compact([
          payout.compensationPlanAssignment?._policies?.delete.can && {
            label: i18n.t('incentives.detail.assignments.remove_additional_compensation.label'),
            icon: Delete,
            critical: true,
            onClick: () => setAssignmentToRemove(payout.compensationPlanAssignment?.id ?? null),
          },
        ]),
    },
    [canCreateAssignment, periodId]
  )

  return (
    <>
      <RemoveAssignmentDialog
        isOpen={!!assignmentToRemove}
        onClose={() => setAssignmentToRemove(null)}
        assignmentId={assignmentToRemove ?? ''}
      />
      <OneDataCollection
        fullHeight
        source={source}
        emptyStates={{
          'no-data': {
            emoji: '💰',
            title: i18n.t('incentives.detail.assignments.empty_state.title'),
            description: i18n.t('incentives.detail.assignments.empty_state.description'),
          },
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('incentives.detail.assignments.list.name'),
                  render: (payout: IncentivesCompensationPlanPayoutFragment) => ({
                    type: 'person' as const,
                    value: {
                      firstName: payout.employee?.firstName ?? '',
                      lastName: payout.employee?.lastName ?? '',
                      avatar: payout.employee?.avatar?.url,
                    },
                  }),
                },
                {
                  label: i18n.t('incentives.detail.assignments.list.job_title'),
                  render: (payout: IncentivesCompensationPlanPayoutFragment) => ({
                    type: 'text' as const,
                    value: payout.employee?.referenceContract?.jobTitle ?? '-',
                  }),
                },
                {
                  label: i18n.t('incentives.detail.assignments.list.amount'),
                  render: (payout: IncentivesCompensationPlanPayoutFragment) => ({
                    type: 'text' as const,
                    value: formatAmount(payout),
                  }),
                },
                {
                  label: i18n.t('incentives.detail.assignments.list.estimated_payout'),
                  align: 'right' as const,
                  render: (payout: IncentivesCompensationPlanPayoutFragment) => ({
                    type: 'text' as const,
                    value: formatCurrency(payout.estimatedAmountCents, getCurrency(payout)),
                  }),
                },
                {
                  label: i18n.t('incentives.detail.assignments.list.payout'),
                  align: 'right' as const,
                  render: (payout: IncentivesCompensationPlanPayoutFragment) => ({
                    type: 'text' as const,
                    value: formatCurrency(payout.amountCents, getCurrency(payout)),
                  }),
                },
              ],
            },
          },
        ]}
      />
    </>
  )
}
